'use client'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { TIP_MAX_AMOUNT, TIP_MIN_AMOUNT } from '@/constants/tips'
import { PUSD_CONTRACT_ADDRESS } from '@/constants/tokens'
import { SOLANA_USDC_MINT } from '@/hooks/kalshi/useDflowSwap'
import { useMarketBalances } from '@/hooks/wallets/use-market-balances'
import { authFetch } from '@/lib/auth/auth-fetch'
import { encodeErc20Transfer } from '@/lib/onchain/erc20'
import {
	createAssociatedTokenAccountInstruction,
	createSplTransferInstruction,
	getAssociatedTokenAddress,
} from '@/lib/onchain/solana-token'
import { BSC_USDT_ADDRESS } from '@/lib/predictfun/client'
import { executeWithRelayerFallback } from '@/lib/relayer-fallback'
import { ensureGasBalance } from '@/lib/solana/ensureGasBalance'
import { sendSponsoredCalls } from '@/lib/wallet-core/sponsored'
import { invalidateMarketBalances } from '@/lib/wallets/invalidation'
import {
	buildErc20TransferCall,
	deriveDepositWalletAddress,
	ensureDepositWalletReady,
	executeDepositWalletBatch,
} from '@/lib/wallets/polymarket-deposit'
import { useTrading } from '@/providers/TradingProvider'
import { useWallet } from '@/providers/WalletContext'
import { useModalStore } from '@/stores/modal-store'
import { resolvePolymarketWalletAddress } from '@/utils/polymarket-wallet-address'
import { captureApiError } from '@/utils/sentry-helpers'
import { OperationType, type SafeTransaction } from '@polymarket/builder-relayer-client'
import {
	PublicKey,
	type TransactionInstruction,
	TransactionMessage,
	VersionedTransaction,
} from '@solana/web3.js'
import { useQueryClient } from '@tanstack/react-query'
import {
	AlertCircle,
	ArrowLeft,
	CheckCircle2,
	ExternalLink,
	Loader2,
	Search,
	User,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Address } from 'viem'

type TipChain = 'polygon' | 'solana' | 'bsc'
type TipStep = 'username' | 'amount' | 'executing' | 'success' | 'error'

interface RecipientUser {
	id: string
	username: string
	avatarLink: string | null
	walletAddress: string
	polymarketSafe: string | null
	// Wallet-switcher preference — /api/user/lookup includes it so tips land
	// on the recipient's ACTIVE Polymarket wallet.
	polymarketActiveWallet: string | null
	solanaAddress: string | null
}

const CHAIN_OPTIONS: { key: TipChain; label: string; sublabel: string }[] = [
	{ key: 'polygon', label: 'Polymarket', sublabel: 'USDC · Polygon' },
	{ key: 'solana', label: 'Kalshi', sublabel: 'USDC · Solana' },
	{ key: 'bsc', label: 'Predict.fun', sublabel: 'USDT · BSC' },
]

const POLYGON_USDC_ADDRESS = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'

function getExplorerUrl(chain: TipChain, txHash: string): string {
	switch (chain) {
		case 'polygon':
			return `https://polygonscan.com/tx/${txHash}`
		case 'solana':
			return `https://solscan.io/tx/${txHash}`
		case 'bsc':
			return `https://bscscan.com/tx/${txHash}`
	}
}

// ---------------------------------------------------------------------------
// Retry helper – retries once on failure with a short delay
// ---------------------------------------------------------------------------

async function withRetry<T>(fn: () => Promise<T>, retries = 1): Promise<T> {
	for (let attempt = 0; attempt <= retries; attempt++) {
		try {
			return await fn()
		} catch (err) {
			if (attempt < retries) {
				console.error(`[Tip] Attempt ${attempt + 1} failed, retrying...`, err)
				await new Promise(r => setTimeout(r, 1000))
			} else {
				throw err
			}
		}
	}
	throw new Error('Unreachable')
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TipModal() {
	const { activeModal, closeModal } = useModalStore()
	const isOpen = activeModal === 'tip'
	const t = useTranslations('tip')

	// Step state
	const [step, setStep] = useState<TipStep>('username')
	const [recipient, setRecipient] = useState<RecipientUser | null>(null)
	const [usernameInput, setUsernameInput] = useState('')
	const [isSearching, setIsSearching] = useState(false)
	const [searchError, setSearchError] = useState<string | null>(null)
	const [chain, setChain] = useState<TipChain>('polygon')
	const [amount, setAmount] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [txHash, setTxHash] = useState<string | null>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Wallet & trading context
	const {
		eoaAddress,
		solanaAddress,
		walletClient,
		publicClient,
		evmAccount,
		solanaConnection,
		solanaSigner,
		polymarketSafe,
		polymarketActiveWallet,
	} = useWallet()
	// Sender-side funding source follows the wallet switcher: legacy users who
	// switched to the deposit wallet must tip from it, not from the Safe.
	const sendsFromSafe = !!polymarketSafe && polymarketActiveWallet !== 'deposit'
	const { relayClient } = useTrading()
	const queryClient = useQueryClient()

	// Balances
	const { balances } = useMarketBalances(['polymarket', 'kalshi', 'predictfun'])
	const polygonRawBalance = balances.polymarket?.cash.raw ?? 0n
	const solanaRawBalance = balances.kalshi?.cash.raw ?? 0n
	const predictFunCash = balances.predictfun?.cash.amount ?? 0

	const polygonBalance = Number(polygonRawBalance) / 1_000_000
	const solanaBalance = Number(solanaRawBalance) / 1_000_000

	const getBalance = useCallback(
		(c: TipChain): number => {
			switch (c) {
				case 'polygon':
					return polygonBalance
				case 'solana':
					return solanaBalance
				case 'bsc':
					return predictFunCash
			}
		},
		[polygonBalance, solanaBalance, predictFunCash]
	)

	const sourceBalance = getBalance(chain)

	// Recipient address for the selected chain
	const recipientAddress = useMemo(() => {
		if (!recipient) return null
		switch (chain) {
			case 'polygon':
				return resolvePolymarketWalletAddress(recipient, recipient.id)
			case 'solana':
				return recipient.solanaAddress
			case 'bsc':
				return recipient.walletAddress
		}
	}, [recipient, chain])

	// Amount validation
	const amountNum = Number.parseFloat(amount) || 0
	const amountError = useMemo(() => {
		if (!amount) return null
		if (amountNum < TIP_MIN_AMOUNT) return t('minAmount', { amount: TIP_MIN_AMOUNT })
		if (amountNum > TIP_MAX_AMOUNT) return t('maxAmount', { amount: TIP_MAX_AMOUNT })
		if (amountNum > sourceBalance) return t('insufficientBalance')
		return null
	}, [amount, amountNum, sourceBalance, t])

	const canSend =
		!!recipient &&
		!!recipientAddress &&
		amountNum >= TIP_MIN_AMOUNT &&
		amountNum <= TIP_MAX_AMOUNT &&
		amountNum <= sourceBalance &&
		step === 'amount'

	// ------------------------------------------------------------------
	// Username lookup (debounced)
	// ------------------------------------------------------------------
	useEffect(() => {
		if (!usernameInput.trim()) {
			setRecipient(null)
			setSearchError(null)
			return
		}

		if (debounceRef.current) clearTimeout(debounceRef.current)

		debounceRef.current = setTimeout(async () => {
			setIsSearching(true)
			setSearchError(null)
			setRecipient(null)
			try {
				const res = await fetch(
					`/api/user/lookup?username=${encodeURIComponent(usernameInput.trim())}`
				)
				if (res.ok) {
					const data = await res.json()
					setRecipient(data.user)
				} else {
					setSearchError(t('userNotFound'))
				}
			} catch {
				setSearchError(t('userNotFound'))
			} finally {
				setIsSearching(false)
			}
		}, 500)

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [usernameInput, t])

	// ------------------------------------------------------------------
	// Record tip in DB
	// ------------------------------------------------------------------
	const recordTip = useCallback(
		async (hash: string) => {
			if (!recipient) return
			try {
				await authFetch('/api/tips', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						recipientUsername: recipient.username,
						amount: amountNum,
						chain,
						txHash: hash,
					}),
				})
			} catch {
				// Non-critical: tip was sent, just DB recording failed
				console.error('Failed to record tip in database')
			}
		},
		[recipient, amountNum, chain]
	)

	// ------------------------------------------------------------------
	// Execute tip transfer
	// ------------------------------------------------------------------
	const handleSend = useCallback(async () => {
		if (!canSend || !recipientAddress) return

		setStep('executing')
		setError(null)
		setTxHash(null)

		try {
			let hash: string | null = null

			if (chain === 'polygon') {
				// Polygon: ERC-20 transfer from the sender's Polymarket wallet
				const amountRaw = BigInt(Math.floor(amountNum * 1_000_000))

				if (sendsFromSafe) {
					// --- Safe path (legacy users) ---
					if (!relayClient) {
						throw new Error('Trading session not initialized. Please set up your wallet first.')
					}

					hash = await withRetry(async () => {
						const safeTx: SafeTransaction = {
							to: POLYGON_USDC_ADDRESS,
							data: encodeErc20Transfer(recipientAddress, amountRaw),
							value: '0',
							operation: OperationType.Call,
						}

						const response = await executeWithRelayerFallback(
							relayClient,
							walletClient,
							[safeTx],
							'Send tip'
						)
						const result = (await response.wait()) as {
							txHash?: string
							transactionHash?: string
						} | null

						const h = result?.txHash || result?.transactionHash || null
						if (!h) throw new Error('Transaction failed on-chain. Please try again.')
						return h
					})
				} else {
					// --- Deposit-wallet path (new users) ---
					if (!(walletClient && publicClient && eoaAddress)) {
						throw new Error('Wallet not connected')
					}
					try {
						const depositWalletAddress = deriveDepositWalletAddress(eoaAddress as Address)
						await ensureDepositWalletReady({ walletClient, publicClient, depositWalletAddress })

						hash = await withRetry(async () => {
							const transferCall = buildErc20TransferCall(
								PUSD_CONTRACT_ADDRESS as Address,
								recipientAddress as Address,
								amountRaw
							)
							const { transactionHash } = await executeDepositWalletBatch(
								walletClient,
								[transferCall],
								depositWalletAddress
							)
							return transactionHash
						})
					} catch (err) {
						captureApiError(err, {
							endpoint: 'polymarket_deposit_tip',
							tags: { category: 'polymarket-deposit', flow: 'tip' },
							extra: { depositWallet: eoaAddress, userId: undefined },
						})
						throw err
					}
				}
			} else if (chain === 'solana') {
				// Solana: SPL token transfer
				if (!(solanaConnection && solanaSigner && solanaAddress)) {
					throw new Error('Solana wallet not available')
				}

				hash = await withRetry(async () => {
					await ensureGasBalance(solanaConnection, solanaAddress, solanaSigner)

					const amountRaw = BigInt(Math.floor(amountNum * 1_000_000))
					const mintPubkey = new PublicKey(SOLANA_USDC_MINT)
					const ownerPubkey = new PublicKey(solanaAddress)
					const recipientPubkey = new PublicKey(recipientAddress)

					const sourceAta = getAssociatedTokenAddress(mintPubkey, ownerPubkey)
					const destAta = getAssociatedTokenAddress(mintPubkey, recipientPubkey)

					const instructions: TransactionInstruction[] = []

					const destAccountInfo = await solanaConnection.getAccountInfo(destAta)
					if (!destAccountInfo) {
						instructions.push(
							createAssociatedTokenAccountInstruction(
								ownerPubkey,
								destAta,
								recipientPubkey,
								mintPubkey
							)
						)
					}

					instructions.push(
						createSplTransferInstruction(sourceAta, destAta, ownerPubkey, amountRaw)
					)

					const { blockhash } = await solanaConnection.getLatestBlockhash()
					const message = new TransactionMessage({
						payerKey: ownerPubkey,
						recentBlockhash: blockhash,
						instructions,
					}).compileToV0Message()

					const tx = new VersionedTransaction(message)
					const signedTx = await solanaSigner.addSignature(tx)

					let signature: string
					try {
						signature = await solanaConnection.sendRawTransaction(signedTx.serialize(), {
							skipPreflight: false,
							preflightCommitment: 'confirmed',
						})
					} catch (err) {
						const msg = err instanceof Error ? err.message : String(err)
						if (msg.includes('Attempt to debit an account but found no record of a prior credit')) {
							throw new Error(
								'Issues landing the transaction on Solana. The chain might be busy, please retry.'
							)
						}
						throw err
					}

					for (let i = 0; i < 30; i++) {
						const txStatus = await solanaConnection.getSignatureStatus(signature)
						if (
							txStatus.value?.confirmationStatus === 'confirmed' ||
							txStatus.value?.confirmationStatus === 'finalized'
						) {
							break
						}
						if (txStatus.value?.err) {
							throw new Error(`Transaction failed: ${JSON.stringify(txStatus.value.err)}`)
						}
						await new Promise(r => setTimeout(r, 1000))
					}

					return signature
				})
			} else {
				// BSC: ERC-20 USDT transfer via EIP-7702 gas-sponsored account
				if (!evmAccount) {
					throw new Error('BSC wallet not available')
				}

				hash = await withRetry(async () => {
					const amountRaw = BigInt(Math.floor(amountNum * 1e18))
					return sendSponsoredCalls({
						chain: 'bsc',
						account: evmAccount,
						calls: {
							target: BSC_USDT_ADDRESS as `0x${string}`,
							data: encodeErc20Transfer(recipientAddress, amountRaw),
						},
					})
				})
			}

			if (hash) {
				setTxHash(hash)
				await recordTip(hash)
			}

			setStep('success')
			invalidateMarketBalances(queryClient, ['polymarket', 'kalshi', 'predictfun'])
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Tip failed'
			setError(message)
			setStep('error')
			console.error('[Tip] Error:', err)
		}
	}, [
		canSend,
		chain,
		amountNum,
		recipientAddress,
		sendsFromSafe,
		relayClient,
		walletClient,
		publicClient,
		eoaAddress,
		solanaConnection,
		solanaSigner,
		solanaAddress,
		evmAccount,
		queryClient,
		recordTip,
	])

	// ------------------------------------------------------------------
	// Reset & close
	// ------------------------------------------------------------------
	const handleReset = useCallback(() => {
		setStep('username')
		setRecipient(null)
		setUsernameInput('')
		setIsSearching(false)
		setSearchError(null)
		setChain('polygon')
		setAmount('')
		setError(null)
		setTxHash(null)
	}, [])

	const handleClose = useCallback(() => {
		handleReset()
		closeModal()
	}, [handleReset, closeModal])

	// ------------------------------------------------------------------
	// Render
	// ------------------------------------------------------------------

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent className="max-w-[440px] border-[#282A2C] bg-brand-dark-light p-0">
				<DialogHeader className="border-[#282A2C] border-b px-[20px] py-[16px]">
					<DialogTitle className="font-medium text-[18px] text-white">{t('title')}</DialogTitle>
					<p className="text-[14px] text-white/50">{t('subtitle')}</p>
				</DialogHeader>

				{/* Step 1: Username Input */}
				{step === 'username' && (
					<div className="flex flex-col gap-[16px] px-[20px] pb-[20px]">
						<div>
							<label htmlFor="tip-username" className="mb-[6px] block text-[13px] text-white/60">
								{t('recipientLabel')}
							</label>
							<div className="relative">
								<Search className="-translate-y-1/2 absolute top-1/2 left-[12px] size-[16px] text-white/40" />
								<Input
									id="tip-username"
									value={usernameInput}
									onChange={e => setUsernameInput(e.target.value)}
									placeholder={t('recipientPlaceholder')}
									className="border-[#282A2C] bg-white/5 pl-[36px] text-white placeholder:text-white/30"
								/>
								{isSearching && (
									<Loader2 className="-translate-y-1/2 absolute top-1/2 right-[12px] size-[16px] animate-spin text-white/40" />
								)}
							</div>
						</div>

						{/* Search result */}
						{recipient && (
							<div className="flex items-center gap-[12px] rounded-[8px] border border-brand-green/30 bg-brand-green/5 p-[12px]">
								<div className="flex size-[36px] items-center justify-center rounded-full bg-white/10">
									{recipient.avatarLink ? (
										<img
											src={recipient.avatarLink}
											alt={recipient.username}
											className="size-[36px] rounded-full object-cover"
										/>
									) : (
										<User className="size-[18px] text-white/60" />
									)}
								</div>
								<div>
									<p className="font-medium text-[14px] text-white">{recipient.username}</p>
									<p className="text-[12px] text-brand-green">{t('userFound')}</p>
								</div>
							</div>
						)}

						{searchError && !isSearching && usernameInput.trim() && (
							<div className="flex items-center gap-[8px] rounded-[8px] border border-brand-red/30 bg-brand-red/5 p-[12px]">
								<AlertCircle className="size-[16px] text-brand-red" />
								<p className="text-[13px] text-brand-red">{searchError}</p>
							</div>
						)}

						<Button
							onClick={() => setStep('amount')}
							disabled={!recipient}
							className="w-full rounded-[8px] bg-brand-blue py-[10px] font-medium text-white hover:bg-brand-blue/90 disabled:opacity-40"
						>
							{t('next')}
						</Button>
					</div>
				)}

				{/* Step 2: Amount & Chain */}
				{step === 'amount' && (
					<div className="flex flex-col gap-[16px] px-[20px] pb-[20px]">
						{/* Back + recipient summary */}
						<button
							type="button"
							onClick={() => setStep('username')}
							className="flex items-center gap-[6px] text-[13px] text-white/50 transition-colors hover:text-white"
						>
							<ArrowLeft className="size-[14px]" />
							{t('back')}
						</button>

						{recipient && (
							<div className="flex items-center gap-[10px] rounded-[8px] bg-white/5 p-[10px]">
								<div className="flex size-[28px] items-center justify-center rounded-full bg-white/10">
									{recipient.avatarLink ? (
										<img
											src={recipient.avatarLink}
											alt={recipient.username}
											className="size-[28px] rounded-full object-cover"
										/>
									) : (
										<User className="size-[14px] text-white/60" />
									)}
								</div>
								<p className="font-medium text-[14px] text-white">
									{t('to')}: {recipient.username}
								</p>
							</div>
						)}

						{/* Chain selector */}
						<div>
							<p className="mb-[6px] text-[13px] text-white/60">{t('selectChain')}</p>
							<div className="flex gap-[8px]">
								{CHAIN_OPTIONS.map(opt => {
									const recAddr =
										opt.key === 'polygon'
											? recipient && resolvePolymarketWalletAddress(recipient, recipient.id)
											: opt.key === 'solana'
												? recipient?.solanaAddress
												: recipient?.walletAddress
									const hasAddress = !!recAddr

									return (
										<button
											key={opt.key}
											type="button"
											onClick={() => hasAddress && setChain(opt.key)}
											disabled={!hasAddress}
											className={`flex-1 rounded-[8px] border p-[10px] text-left transition-colors ${
												chain === opt.key
													? 'border-brand-blue bg-brand-blue/10'
													: hasAddress
														? 'border-[#282A2C] bg-white/3 hover:border-white/20'
														: 'cursor-not-allowed border-[#282A2C] bg-white/2 opacity-40'
											}`}
										>
											<p className="font-medium text-[13px] text-white">{opt.label}</p>
											<p className="text-[11px] text-white/50">
												{hasAddress ? opt.sublabel : t('noAddressForChain')}
											</p>
										</button>
									)
								})}
							</div>
						</div>

						{/* Balance display */}
						<div className="flex items-center justify-between text-[13px]">
							<span className="text-white/50">{t('availableBalance')}</span>
							<span className="font-medium text-white">${sourceBalance.toFixed(2)}</span>
						</div>

						{/* Amount input */}
						<div>
							<label htmlFor="tip-amount" className="mb-[6px] block text-[13px] text-white/60">
								{t('amount')}
							</label>
							<div className="relative">
								<span className="-translate-y-1/2 absolute top-1/2 left-[12px] font-medium text-[16px] text-white/50">
									$
								</span>
								<Input
									id="tip-amount"
									type="number"
									value={amount}
									onChange={e => setAmount(e.target.value)}
									placeholder={t('amountPlaceholder')}
									min={TIP_MIN_AMOUNT}
									max={TIP_MAX_AMOUNT}
									step="0.01"
									className="border-[#282A2C] bg-white/5 pl-[28px] text-[16px] text-white placeholder:text-white/30"
								/>
							</div>
							{amountError && <p className="mt-[4px] text-[12px] text-brand-red">{amountError}</p>}
						</div>

						{!recipientAddress && recipient && (
							<div className="flex items-center gap-[8px] rounded-[8px] border border-amber-500/30 bg-amber-500/5 p-[10px]">
								<AlertCircle className="size-[14px] text-amber-500" />
								<p className="text-[12px] text-amber-500">{t('noAddressForChain')}</p>
							</div>
						)}

						<Button
							onClick={handleSend}
							disabled={!canSend}
							className="w-full rounded-[8px] bg-brand-blue py-[10px] font-medium text-white hover:bg-brand-blue/90 disabled:opacity-40"
						>
							{t('sendTip')}
						</Button>
					</div>
				)}

				{/* Step 3: Executing */}
				{step === 'executing' && (
					<div className="flex flex-col items-center gap-[16px] px-[20px] py-[32px]">
						<Loader2 className="h-12 w-12 animate-spin text-brand-blue" />
						<div className="text-center">
							<p className="font-medium text-[18px] text-white">{t('processing')}</p>
							<p className="mt-[8px] text-[14px] text-white/70 text-wrapper">
								{t('processingDescription', {
									username: recipient?.username ?? '',
								})}
							</p>
						</div>
					</div>
				)}

				{/* Step 4: Success */}
				{step === 'success' && (
					<div className="flex flex-col items-center gap-[16px] px-[20px] py-[32px]">
						<CheckCircle2 className="h-12 w-12 text-brand-green" />
						<div className="text-center">
							<p className="font-medium text-[18px] text-white">{t('success')}</p>
							<p className="mt-[8px] text-[14px] text-white/70 text-wrapper">
								{t('successMessage', {
									amount: amountNum.toFixed(2),
									username: recipient?.username ?? '',
								})}
							</p>
						</div>
						{txHash && (
							<a
								href={getExplorerUrl(chain, txHash)}
								target="_blank"
								rel="noopener noreferrer"
								className="flex items-center gap-[6px] text-[13px] text-white hover:underline"
							>
								{t('viewTransaction')}
								<ExternalLink className="size-[12px]" />
							</a>
						)}
						<Button
							onClick={handleClose}
							className="w-full rounded-[8px] bg-brand-blue py-[10px] font-medium text-white hover:bg-brand-blue/90"
						>
							{t('done')}
						</Button>
					</div>
				)}

				{/* Step 5: Error */}
				{step === 'error' && (
					<div className="flex flex-col items-center gap-[16px] px-[20px] py-[32px]">
						<AlertCircle className="h-12 w-12 text-brand-red" />
						<div className="text-center">
							<p className="font-medium text-[18px] text-white">{t('error')}</p>
							<p className="mt-[8px] text-[14px] text-white/70 text-wrapper">{error}</p>
						</div>
						<div className="flex w-full gap-[8px]">
							<Button
								onClick={() => setStep('amount')}
								className="flex-1 rounded-[8px] border border-white/10 bg-white/7 py-[10px] font-medium text-white hover:bg-white/10"
							>
								{t('back')}
							</Button>
							<Button
								onClick={handleSend}
								className="flex-1 rounded-[8px] bg-brand-blue py-[10px] font-medium text-white hover:bg-brand-blue/90"
							>
								{t('sendTip')}
							</Button>
						</div>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
